/** Installer-side "My Portfolio" — keeps growing after approval; every project is visible to Ox Resin staff. */
import React, { useCallback, useEffect, useState } from 'react';
import { Images, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import API_URL from '@/config/api';
import { formatApiError } from '@/utils/apiError';
import { ProjectCard } from './ProjectCard';
import { MARKETING_NOTE, ProjectDialog } from './ProjectDialog';

export function PortfolioSection({ brand, theme, labels = {} }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // null | 'new' | project

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/installer/portfolio?brand=${brand}`, { credentials: 'include' });
      if (res.ok) setProjects((await res.json()).projects || []);
    } finally { setLoading(false); }
  }, [brand]);
  useEffect(() => { load(); }, [load]);

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}" and its photos?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/installer/portfolio/${p.id}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new Error(formatApiError(await res.json()) || 'Failed');
      toast.success('Project deleted.'); load();
    } catch (e) { toast.error(e.message); }
  };
  const removePhoto = (p) => async (ph) => {
    try {
      const res = await fetch(`${API_URL}/api/installer/portfolio/${p.id}/photos/${ph.id}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new Error(formatApiError(await res.json()) || 'Failed');
      load();
    } catch (e) { toast.error(e.message); }
  };

  const mine = projects.filter((p) => p.source === 'portfolio');
  return (
    <Card className="p-4 sm:p-5" data-testid="portfolio-section">
      <div className="flex flex-wrap items-start gap-3 justify-between">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: `${theme.buttonBg}1A`, color: theme.buttonBg }}><Images className="w-5 h-5" /></div>
          <div>
            <h3 className="font-bold text-base text-gray-900">My {theme.label} Portfolio</h3>
            <p className="text-xs sm:text-sm text-gray-600 mt-0.5">Keep adding finished projects — before, during and after shots. Ox Resin see every project you add, even after you're approved.</p>
          </div>
        </div>
        <Button onClick={() => setDialog('new')} className="font-semibold shrink-0" style={{ background: theme.buttonBg, color: theme.buttonText }} data-testid="portfolio-add-btn"><Plus className="w-4 h-4 mr-1.5" /> Add project</Button>
      </div>
      <p className="mt-3 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5" data-testid="portfolio-marketing-note">{MARKETING_NOTE}</p>
      <div className="mt-4 space-y-3">
        {loading ? <div className="text-sm text-gray-500">Loading…</div> : projects.length === 0 ? (
          <div className="text-sm text-gray-500 italic" data-testid="portfolio-empty">No projects yet — add your first one.</div>
        ) : projects.map((p) => (
          <ProjectCard key={p.id} project={p} onRemovePhoto={p.source === 'portfolio' ? removePhoto(p) : null}
            actions={p.source === 'portfolio' ? (
              <>
                <Button size="sm" variant="outline" onClick={() => setDialog(p)} data-testid={`portfolio-edit-${p.id}`}><Pencil className="w-3.5 h-3.5 mr-1" /> Edit / add photos</Button>
                <Button size="sm" variant="outline" onClick={() => remove(p)} className="border-rose-300 text-rose-700" aria-label="Delete project" data-testid={`portfolio-delete-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
              </>
            ) : null} />
        ))}
        {!loading && mine.length > 0 && <div className="text-[11px] text-gray-500">{mine.length} project{mine.length === 1 ? '' : 's'} added by you{projects.length > mine.length ? ' + your approval project' : ''}.</div>}
      </div>
      <ProjectDialog open={!!dialog} onClose={() => { setDialog(null); load(); }} brand={brand} theme={theme} labels={labels} project={dialog && dialog !== 'new' ? dialog : null} onSaved={() => load()} />
    </Card>
  );
}
